const POLLING_INTERVAL_MS = 3000;

type Ref<T> = { current: T };

type RefetchQuery<TData> = { state: { data?: TData; error?: unknown } };

type RefetchIntervalOptions<TData> = {
  onStopRef: Ref<(() => void) | undefined>;
  attemptCountRef: Ref<number>;
  startTimeRef: Ref<number>;
  maxAttempts: number | undefined;
  maxDurationMs: number | undefined;
  keepPollingOnError: boolean;
  /**
   * When polling is finished for the given data. Defaults to "every value is truthy",
   * which fits the active-flag response shapes; list responses need their own predicate.
   */
  isDone?: (data: TData) => boolean;
};

function everyValueTruthy(data: unknown) {
  const values = Object.values(data ?? {});
  return values.length > 0 && values.every(Boolean);
}

export function makeRefetchInterval<TData = unknown>({
  onStopRef,
  attemptCountRef,
  startTimeRef,
  maxAttempts,
  maxDurationMs,
  keepPollingOnError,
  isDone = everyValueTruthy,
}: RefetchIntervalOptions<TData>): (query: RefetchQuery<TData>) => number | false {
  return (query: RefetchQuery<TData>) => {
    if (query.state.data !== undefined && isDone(query.state.data)) {
      onStopRef.current?.();
      return false;
    }

    if (maxAttempts !== undefined) {
      if (!query.state.error || attemptCountRef.current >= maxAttempts) {
        onStopRef.current?.();
        return false;
      }
    } else if (maxDurationMs !== undefined) {
      if (query.state.error) {
        onStopRef.current?.();
        return false;
      }
      // Guard against startTimeRef being uninitialized (0) — can happen if refetchInterval
      // fires from cached data before the useEffect above has run.
      if (startTimeRef.current > 0 && Date.now() - startTimeRef.current >= maxDurationMs) {
        onStopRef.current?.();
        return false;
      }
    } else if (query.state.error && !keepPollingOnError) {
      // Keep polling through transient errors when opted in; a later poll recovers on its own.
      // Reporting the failure is the query cache's job — see report-poll-error.
      onStopRef.current?.();
      return false;
    }

    return POLLING_INTERVAL_MS;
  };
}
